import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { PortableTextBlock, PortableTextTextBlock } from "@sanity/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getSlugFromType(type: string, slug: string): string {
  switch (type) {
    case "article":
      return `/insight/${slug}`;
    case "qaPost":
      return `/qa/${slug}`;
    case "interview":
      return `/interview/${slug}`;
    case "newsPost":
      return `/news/${slug}`;
    default:
      console.error("Type not found!", type, slug);
      throw new Error("Type not found!");
  }
}

export function convertPortableTextBlockToPlaintext(
  blocks: PortableTextBlock[]
): string {
  return (
    blocks
      // loop through each block
      .map((block) => {
        // if it's not a text block with children,
        // return nothing
        if (block._type !== "block" || !block.children) {
          return "";
        }
        // loop through the children spans, and join the
        // text strings
        else
          return (block as PortableTextTextBlock).children
            .map((child) => child.text)
            .join("");
      })
      // join the paragraphs leaving split by two linebreaks
      .join("\n\n")
  );
}

export const questionStatusList = [
  "Entered",
  "In Progress",
  "Discarded",
  "Published",
  "Completed",
  "Scheduled",
] as const;
export const attributionList = [
  "NoFollow",
  "DoFollow",
  "Unknown",
  "Unlinked",
  "Sponsored",
] as const;
export const articleTypeList = ["Quotes Only", "Article"] as const;

export function rtrim(input: string, delimiter: string): string {
  // Escape regex characters in the delimiter
  const escaped = delimiter.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
  const regex = new RegExp(`[${escaped}]+$`, "g");
  return input.replace(regex, "");
}

import { CSSProperties } from "react";

/**
 * Converts a kebab-cased string to camelCase.
 * Example: 'font-size' -> 'fontSize'
 * @param {string} str - The kebab-cased string.
 * @returns {string} The camelCased string.
 */
function toCamelCase(str: string): string {
  return str.replace(/-(\w)/g, (_, char) => char.toUpperCase());
}

/**
 * Converts a string of CSS properties into a React.CSSProperties object.
 * @param {string} cssString - The CSS property string (e.g., "color: #222; background: orange; font-size: 2em;").
 * @returns {React.CSSProperties} The converted object.
 */
export function convertCssStringToObject(
  cssString: string
): React.CSSProperties {
  if (!cssString) {
    return {};
  }

  const result: Record<string, string> = {};

  // Split by semicolon to get individual declarations and filter out any empty entries
  const declarations = cssString
    .split(";")
    .filter((declaration) => declaration.trim() !== "");

  for (const declaration of declarations) {
    const [property, value] = declaration.split(":").map((part) => part.trim());

    if (property && value) {
      // Convert the property name to camelCase for React's style object
      const camelCasedProperty = toCamelCase(property);
      result[camelCasedProperty] = value;
    }
  }

  // Type-cast the final object to React.CSSProperties
  return result as React.CSSProperties;
}
